/**
 * Light/dark theme provider. Toggles the `dark` class on <html> (Tailwind
 * `darkMode: 'class'`). Until the operator presses the toggle, the theme
 * follows the time of day — light 07:00–18:59, dark otherwise — re-evaluated
 * every minute (Kliment to Magda, 16 Sep call: "автоматски според day of
 * time"). A press stores an explicit choice, which then wins.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type Theme = 'light' | 'dark';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);
const STORAGE_KEY = 'yd_admin_theme';

/** Daylight hours are light; the rest is dark. Pure, so it is testable. */
export function themeForHour(hour: number): Theme {
  return hour >= 7 && hour < 19 ? 'light' : 'dark';
}

function readStoredTheme(): Theme | null {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === 'light' || stored === 'dark' ? stored : null;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(
    () => readStoredTheme() ?? themeForHour(new Date().getHours()),
  );

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  // Follow the clock only while no explicit choice is stored.
  useEffect(() => {
    if (readStoredTheme()) return;
    const id = window.setInterval(() => {
      if (readStoredTheme()) return;
      setThemeState(themeForHour(new Date().getHours()));
    }, 60_000);
    return () => window.clearInterval(id);
  }, []);

  const setTheme = useCallback((next: Theme) => {
    localStorage.setItem(STORAGE_KEY, next);
    setThemeState(next);
  }, []);
  const toggleTheme = useCallback(
    () =>
      setThemeState((t) => {
        const next = t === 'dark' ? 'light' : 'dark';
        localStorage.setItem(STORAGE_KEY, next);
        return next;
      }),
    [],
  );

  const value = useMemo(
    () => ({ theme, setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a <ThemeProvider>.');
  return ctx;
}
